import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Table, Badge, Modal, ProgressBar, ButtonGroup } from 'react-bootstrap';
import api from './api';

const CommunityNotesUpload = () => {
  const [courses, setCourses] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [title, setTitle] = useState('');
  const [link, setLink] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [myNotes, setMyNotes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [uploadMethod, setUploadMethod] = useState('direct'); // 'direct' or 'presigned'
  
  const userLogin = useSelector(state => state.userLogin);
  const { userInfo } = userLogin;
  const navigate = useNavigate();

  useEffect(() => {
    if (userInfo) {
      fetchCourses();
      fetchMyNotes();
    }
  }, [userInfo]);

  useEffect(() => {
    if (selectedCourse) {
      fetchTopics(selectedCourse);
    } else {
      setTopics([]);
      setSelectedTopic('');
    }
  }, [selectedCourse]);

  const fetchCourses = async () => {
    try {
      const response = await api.get('api/courses/');
      setCourses(response.data || []);
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };

  const fetchTopics = async (courseId) => {
    try {
      const response = await api.get(`api/list_topics/${courseId}`);
      setTopics(response.data || []);
    } catch (err) {
      console.error('Error fetching topics:', err);
    }
  };

  const fetchMyNotes = async () => {
    try {
      const config = {
        headers: {
          'Authorization': `Bearer ${userInfo.token}`
        }
      };
      
      const response = await api.get('api/community-notes/my-notes/', config);
      setMyNotes(response.data || []);
    } catch (err) {
      console.error('Error fetching my notes:', err);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = ['.pdf', '.docx', '.md', '.txt', '.html'];
      const fileExtension = selectedFile.name.toLowerCase().match(/\.[^.]+$/)?.[0];
      
      if (!fileExtension || !allowedTypes.includes(fileExtension)) {
        setError('File type not supported. Allowed: PDF, DOCX, MD, TXT, HTML');
        setFile(null);
        return;
      }
      
      if (selectedFile.size > 10 * 1024 * 1024) { // 10MB limit
        setError('File size must be less than 10MB');
        setFile(null);
        return;
      }
      
      setFile(selectedFile);
      setError('');
    }
  };

  const handleDirectUpload = async () => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('course_id', selectedCourse);
    formData.append('topic_id', selectedTopic);
    if (link) {
      formData.append('link', link);
    }

    const config = {
      headers: {
        'Authorization': `Bearer ${userInfo.token}`,
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
      }
    };

    const response = await api.post('api/community-notes/upload/', formData, config);
    return response.data;
  };

  const handlePresignedUpload = async () => {
    const config = {
      headers: {
        'Authorization': `Bearer ${userInfo.token}`,
        'Content-Type': 'application/json'
      }
    };

    // Step 1: Get upload URL
    setUploadProgress(10);
    const uploadUrlResponse = await api.post('api/community-notes/upload-url/', {
      filename: file.name,
      content_type: file.type
    }, config);

    const { upload_url, s3_key } = uploadUrlResponse.data;

    // Step 2: Upload to S3
    setUploadProgress(30);
    await fetch(upload_url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });

    // Step 3: Submit note details
    setUploadProgress(70);
    const submitData = {
      title: title,
      course_id: selectedCourse,
      topic_id: selectedTopic,
      s3_key: s3_key,
      filename: file.name,
      file_type: file.name.split('.').pop().toUpperCase(),
      file_size: file.size
    };
    if (link) {
      submitData.link = link;
    }
    const response = await api.post('api/community-notes/submit/', submitData, config);

    setUploadProgress(100);
    return response.data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file || !title || !selectedCourse || !selectedTopic) {
      setError('Please fill all fields and select a file');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');
    setUploadProgress(0);

    try {
      let result;
      if (uploadMethod === 'direct') {
        result = await handleDirectUpload();
      } else {
        result = await handlePresignedUpload();
      }

      setSuccess(
        uploadMethod === 'direct' 
          ? 'Document uploaded and submitted successfully! It will be reviewed before being published.'
          : 'Document submitted successfully! It will be reviewed before being published.'
      );
      
      // Reset form
      setTitle('');
      setLink('');
      setFile(null);
      setSelectedCourse('');
      setSelectedTopic('');
      document.getElementById('fileInput').value = '';
      
      // Refresh my notes
      fetchMyNotes();
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload document');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 2000);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'warning',
      approved: 'info',
      published: 'success',
      rejected: 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const viewNoteDetails = (note) => {
    setSelectedNote(note);
    setShowModal(true);
  };

  if (!userInfo) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">
          Please log in to upload community notes.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4 px-3 pb-5">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="mb-0">Upload Community Notes</h2>
            <Button 
              variant="secondary" 
              onClick={() => navigate('/community-notes')}
            >
              <i className="bi bi-arrow-left"></i> Back to Community Notes
            </Button>
          </div>
          
          {/* Upload Form */}
          <Card className="mb-4">
            <Card.Header>
              <h5>Submit a Document for Review</h5>
            </Card.Header>
            <Card.Body>
              {/* Upload Method Selection */}
              <div className="mb-4">
                <Form.Label className="fw-bold">Upload Method</Form.Label>
                <ButtonGroup className="d-block">
                  <Button 
                    variant={uploadMethod === 'direct' ? 'primary' : 'outline-primary'}
                    onClick={() => setUploadMethod('direct')}
                    className="me-2"
                  >
                    📤 Direct Upload (Recommended)
                  </Button>
                  <Button 
                    variant={uploadMethod === 'presigned' ? 'primary' : 'outline-primary'}
                    onClick={() => setUploadMethod('presigned')}
                  >
                    🔗 Presigned URL (Large Files)
                  </Button>
                </ButtonGroup>
                <Form.Text className="text-muted d-block mt-2">
                  {uploadMethod === 'direct' 
                    ? '✅ Simple one-step upload through our server. Best for most files.'
                    : '⚡ Two-step upload directly to S3. Better for very large files (>50MB).'
                  }
                </Form.Text>
              </div>

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Document Title *</Form.Label>
                      <Form.Control
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Enter a descriptive title"
                        required
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Select File *</Form.Label>
                      <Form.Control
                        id="fileInput"
                        type="file"
                        onChange={handleFileChange}
                        accept=".pdf,.docx,.md,.txt,.html"
                        required
                      />
                      <Form.Text className="text-muted">
                        Allowed: PDF, DOCX, MD, TXT, HTML (Max 10MB)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>External Link (Optional)</Form.Label>
                      <Form.Control
                        type="url"
                        value={link}
                        onChange={(e) => setLink(e.target.value)}
                        placeholder="https://example.com/resource"
                      />
                      <Form.Text className="text-muted">
                        Add an external link associated with this document (e.g., source website, related resources)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Course *</Form.Label>
                      <Form.Select
                        value={selectedCourse}
                        onChange={(e) => setSelectedCourse(e.target.value)}
                        required
                      >
                        <option value="">Select a course</option>
                        {courses.map(course => (
                          <option key={course.id} value={course.id}>
                            {course.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Topic *</Form.Label>
                      <Form.Select
                        value={selectedTopic}
                        onChange={(e) => setSelectedTopic(e.target.value)}
                        disabled={!selectedCourse}
                        required
                      >
                        <option value="">Select a topic</option>
                        {topics.map(topic => (
                          <option key={topic.id} value={topic.id}>
                            {topic.title}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}
                
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} className="mb-3" />
                )}
                
                <Button 
                  type="submit" 
                  variant="primary" 
                  disabled={uploading || !file}
                >
                  {uploading ? 'Uploading...' : 'Submit for Review'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
          
          {/* My Submitted Notes */}
          <Card>
            <Card.Header>
              <h5>My Submitted Notes</h5>
            </Card.Header>
            <Card.Body>
              {myNotes.length === 0 ? (
                <p className="text-muted">You haven't submitted any notes yet.</p>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Course</th>
                      <th>Topic</th>
                      <th>Status</th>
                      <th>Submitted</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myNotes.map(note => (
                      <tr key={note.id}>
                        <td>{note.title}</td>
                        <td>{note.course}</td>
                        <td>{note.topic}</td>
                        <td>{getStatusBadge(note.status)}</td>
                        <td>{new Date(note.created_at).toLocaleDateString()}</td>
                        <td>
                          <Button 
                            size="sm" 
                            variant="outline-primary"
                            onClick={() => viewNoteDetails(note)}
                          >
                            View Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Note Details Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedNote?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedNote && (
            <div>
              <p><strong>File:</strong> {selectedNote.file_name}</p>
              <p><strong>Type:</strong> {selectedNote.file_type}</p>
              <p><strong>Size:</strong> {(selectedNote.file_size / 1024).toFixed(2)} KB</p>
              <p><strong>Course:</strong> {selectedNote.course}</p>
              <p><strong>Topic:</strong> {selectedNote.topic}</p>
              <p><strong>Status:</strong> {getStatusBadge(selectedNote.status)}</p>
              {selectedNote.review_notes && (
                <div>
                  <p><strong>Review Notes:</strong></p>
                  <Alert variant={selectedNote.status === 'rejected' ? 'danger' : 'info'}>
                    {selectedNote.review_notes}
                  </Alert>
                </div>
              )}
              {selectedNote.xp_awarded && (
                <Alert variant="success">
                  You earned {selectedNote.xp_awarded} XP for this contribution!
                </Alert>
              )}
              {selectedNote.preview_text && (
                <div>
                  <p><strong>Preview:</strong></p>
                  <Card className="bg-light">
                    <Card.Body>
                      <p className="small">{selectedNote.preview_text}</p>
                    </Card.Body>
                  </Card>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CommunityNotesUpload;